import { isEnvIn, isDeployed, perEnv } from './utils'
import { envNames } from './envNames'

/**
 * @object config
 * @category Config
 * @description Repertoire for values which are direct functions of environment variables, so that we document what differs between the different environments.
 * For debugging purposes, we add process.env.NODE_ENV here, but don't use them in the app directly!
 * If you need a value to change based on the deployment environment:
 * - Determine what that value is going to be used for
 * - Give it a meaningful name
 * - Use that name to define a variable in this config object
 * - Define the values of that variable in each environment here
 * - Use the config object in the app to inject the value.
 */
const config = {
  NODE_ENV: process.env.NODE_ENV,
  BASE_URL: perEnv({
    [envNames.DEV]: 'http://localhost:8080',
    [envNames.TEST]: 'http://localhost:8080',
    [envNames.DEMO]: 'https://demo.recright.com',
    [envNames.STAGING]: 'https://staging.recright.com',
    [envNames.PROD]: 'https://www.recright.com',
  }),
  USER_UPLOADS_BASE_URL: perEnv({
    [envNames.DEV]: 'http://localhost:8080/img/video_images',
    [envNames.TEST]: 'http://localhost:8080/img/video_images',
    [envNames.DEMO]: 'https://demo-uploads.recright.com',
    [envNames.STAGING]: 'https://staging-uploads.recright.com',
    [envNames.PROD]: 'https://uploads.recright.com',
  }),
  API_ENDPOINT: perEnv({
    [envNames.DEV]: '/api',
    [envNames.TEST]: '/api',
    [envNames.DEMO]: '/api',
    [envNames.STAGING]: '/api',
    [envNames.PROD]: '/api',
  }),
  IMG_BASE_URL: perEnv({
    [envNames.DEV]: 'http://localhost:8080/img/video_images/b2c-logos',
    [envNames.TEST]: 'http://localhost:8080/img/video_images/b2c-logos',
    [envNames.DEMO]: 'https://demo-uploads.recright.com/b2c-logos',
    [envNames.STAGING]: 'https://staging-uploads.recright.com/b2c-logos',
    [envNames.PROD]: 'https://uploads.recright.com/b2c-logos',
  }),
  VIDEO_URL_BASE: (() => {
    const videoHost = perEnv({
      [envNames.DEV]: 'localhost:1935',
      [envNames.TEST]: 'localhost:1935',
      [envNames.DEMO]: 'demovideo.recruitby.net',
      [envNames.STAGING]: 'demovideo.recruitby.net', // TODO: Check that this makes sense
      [envNames.PROD]: 'video.recruitby.net',
    })

    return `https://${videoHost}/b2cmobile`
  })(),
  USE_HLS: perEnv({
    [envNames.DEV]: false,
    [envNames.TEST]: false,
    [envNames.DEMO]: false,
    [envNames.STAGING]: false,
    [envNames.PROD]: false,
  }),
  SENTRY_DSN: perEnv({
    [envNames.DEV]: '',
    [envNames.TEST]: '',
    [envNames.DEMO]:
      'https://b611d9f62f484e928b9ac95a858091ea@sentry.io/114676',
    [envNames.STAGING]:
      'https://b611d9f62f484e928b9ac95a858091ea@sentry.io/114676', // TODO: Check that this makes sense
    [envNames.PROD]:
      'https://222393dc93bc4e4b8921ffd5bd6f03b7@sentry.io/121841',
  }),
  LINKEDIN_CLIENT_ID: perEnv({
    [envNames.DEV]: '86gw4f0rdchmvo',
    [envNames.TEST]: '86gw4f0rdchmvo',
    [envNames.DEMO]: '867gfsqrnqxt71',
    [envNames.STAGING]: '867gfsqrnqxt71', // TODO: Check that this makes sense
    [envNames.PROD]: '78r0a7wnzjhvk9',
  }),
  CRONOFY_CLIENT_ID: perEnv({
    [envNames.DEV]: '6DlMzKyXzrlAlmAzFc8NmDhrQusZ4Bmh',
    [envNames.TEST]: '6DlMzKyXzrlAlmAzFc8NmDhrQusZ4Bmh',
    [envNames.DEMO]: 'BAyPMxFuObxGm-lGRhAk43HBd_mmnufm',
    [envNames.STAGING]: 'BAyPMxFuObxGm-lGRhAk43HBd_mmnufm', // TODO: Check that this makes sense
    [envNames.PROD]: 'cLHVKXZ6fi3D3zFPLK3psCf2WG7W2N0U',
  }),
  WEB_SOCKET_URI: perEnv({
    [envNames.DEV]: 'ws://localhost:3000/ws',
    [envNames.TEST]: 'ws://localhost:3000/ws',
    [envNames.DEMO]: 'wss://demo.recright.com/ws',
    [envNames.STAGING]: 'wss://staging.recright.com/ws', // TODO: Check that this exists
    [envNames.PROD]: 'wss://www.recright.com/ws',
  }),
  DISABLE_SEQUENCED_INTERVIEW_AUTOMATIC_ARCHIVING: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: undefined,
    [envNames.STAGING]: undefined,
    [envNames.PROD]: undefined,
  }),
  ENABLE_CS_STATISTICS: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),
  ENABLE_COMPANY_EMAIL_TEMPLATES: perEnv({
    [envNames.DEMO]: true,
    [envNames.TEST]: true,
    [envNames.DEV]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),
  ANALYTICS: perEnv({
    /**
     * Amplitude is disabled in dev environment by default.
     * If you need to do local testing (either with envNames.DEV or envNames.TEST),
     * you can enable it by uncommenting the amplitudeId below.
     * All dev environment events will go to a separate
     * "Recright Test" project in Amplitude so that they
     * don't get mixed up with demo or production data.
     * */
    [envNames.DEV]: {
       amplitudeId: 'ba61a94c6ceb1a675129138c6e8c2e94',
    },
    [envNames.TEST]: {
      // amplitudeId: 'ba61a94c6ceb1a675129138c6e8c2e94',
    },
    [envNames.DEMO]: {
      amplitudeId: '03768514c7ec030384d39457403c8987',
    },
    [envNames.STAGING]: {
      amplitudeId: '03768514c7ec030384d39457403c8987',
    },
    [envNames.PROD]: {
      amplitudeId: 'cb48a987d80dd17442a375f1b9d29b85',
      facebookPixelId: '864639300347655',
      linkedInPixelId: '30598',
      twitterPixelId: 'nvq6x',
      segmentPixelId: '26255691',
      match2OnePixelId: '1471158',
      match2OneSegmentId: '26255690',
    },
  }),
  INVISIBLE_RE_CAPTCHA_KEY: perEnv({
    [envNames.DEV]: '6LcoGMIfAAAAAHlKRuCm6S3GfpZTxWtI_ROI4PqW',
    [envNames.TEST]: '6LcoGMIfAAAAAHlKRuCm6S3GfpZTxWtI_ROI4PqW',
    [envNames.DEMO]: '6LcoGMIfAAAAAHlKRuCm6S3GfpZTxWtI_ROI4PqW',
    [envNames.STAGING]: '6LcoGMIfAAAAAHlKRuCm6S3GfpZTxWtI_ROI4PqW',
    [envNames.PROD]: '6LcoGMIfAAAAAHlKRuCm6S3GfpZTxWtI_ROI4PqW',
  }),
  ENABLE_USERS_EXPORT: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),
  INTERCOM_APP_ID: perEnv({
    [envNames.DEV]: 'ka24ge08',
    [envNames.TEST]: 'ka24ge08',
    [envNames.DEMO]: 'gr6me6g5',
    [envNames.STAGING]: 'gr6me6g5',
    [envNames.PROD]: 'gr6me6g5',
  }),
  INTERCOM_COOKIE_DOMAIN: perEnv({
    [envNames.DEV]: 'localhost',
    [envNames.TEST]: 'localhost',
    [envNames.DEMO]: '.recright.com',
    [envNames.STAGING]: '.recright.com',
    [envNames.PROD]: '.recright.com',
  }),
  IS_ERROR_LOGGER_ENABLED: !isDeployed,
  SHOULD_INSTALL_RAVEN: isDeployed,
  IS_REACT_GA_IN_TEST_MODE: isEnvIn(envNames.TEST),
  ADD_ADMIN_WRAPPER: !isDeployed,
  TIMED_INTERVIEW_ALLOW_NOT_ANSWERABLE_QUESTIONS: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),

  ENABLE_RESPONSE_INDIVIDUAL_SUBMIT: perEnv({
    [envNames.DEV]: false,
    [envNames.TEST]: false,
    [envNames.DEMO]: false,
    [envNames.STAGING]: false,
    [envNames.PROD]: false,
  }),

  SHOW_JOYRIDE_TUTORIALS: perEnv({
    [envNames.DEV]: false,
    [envNames.TEST]: false,
    [envNames.DEMO]: false,
    [envNames.STAGING]: false,
    [envNames.PROD]: false,
  }),

  SHOW_TUTORIALS_BUTTON_IN_NAVBAR: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),

  ENABLE_NEW_EMBEDDED_PROFILE_INTERVIEW: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),

  INTERCOM_TOURS: perEnv({
    /**
     * Intercom tours do not work in dev env because they exist only in the prod workspace that does not work in dev.
     * */
    [envNames.DEV]: {
      en: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      se: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      fi: {
        dashboard: 1506,
        jobAdEdit: 2728,
        interviewEdit: 2721,
        candidates: 2727,
        applications: 2729,
      },
    },
    [envNames.TEST]: {
      en: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      se: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      fi: {
        dashboard: 1506,
        jobAdEdit: 2728,
        interviewEdit: 2721,
        candidates: 2727,
        applications: 2729,
      },
    },
    [envNames.DEMO]: {
      en: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      se: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      fi: {
        dashboard: 1506,
        jobAdEdit: 2728,
        interviewEdit: 2721,
        candidates: 2727,
        applications: 2729,
      },
    },
    [envNames.STAGING]: {
      en: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      se: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      fi: {
        dashboard: 1506,
        jobAdEdit: 2728,
        interviewEdit: 2721,
        candidates: 2727,
        applications: 2729,
      },
    },
    [envNames.PROD]: {
      en: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      se: {
        dashboard: 1440,
        jobAdEdit: 2464,
        interviewEdit: 2459,
        candidates: 2462,
        applications: 2465,
      },
      fi: {
        dashboard: 1506,
        jobAdEdit: 2728,
        interviewEdit: 2721,
        candidates: 2727,
        applications: 2729,
      },
    },
  }),

  SHOW_BLUR_FOR_VIRTUAL_BG: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),

  // enables the virtual bg feature per env
  ENABLE_VIRTUAL_BG: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),
  DISABLE_CANDIDATE_ANALYTICS: perEnv({
    [envNames.DEV]: true,
    [envNames.TEST]: true,
    [envNames.DEMO]: true,
    [envNames.STAGING]: true,
    [envNames.PROD]: true,
  }),
  // MAX_CONCATENATED_SMS_MESSAGES 3 is max possible. RR-1568 it was decided to limit to 2.
  MAX_CONCATENATED_SMS_MESSAGES: 2,
  CONCATENATED_SMS_MESSAGES_VONAGE_LIMIT: 3,
  // RR-2083 it was decided to increase limit to 3 for some countries
  COUNTRY_CODES_FOR_INCREASED_SMS_LIMIT: [
    'ar',
    'bg',
    'ru',
    'cs',
    'el',
    'hr',
    'pl',
    'ro',
    'tr',
    'uk',
    'sk',
    'sl',
  ],
  EXCLUDED_SMS_COUNTRY_CODES: {
    'mg-MG': 'mg', // +261
    'tg-TJ': 'tj', // +992
    'fa-AF': 'af', // +93
    'az-AZ': 'az', // +994
    'ar-LY': 'ly', // +218
    'en-PK': 'pk', // +92
    'dz-BT': 'bt', // +975
    'ar-IQ': 'iq', // +964
    'fa-IR': 'ir', // +98
    'ir-IR': 'ir', // +98
    'ar-PS': 'ps', // +970
    'ar-LB': 'lb', // +961
    'ru-RU': 'ru', // +7
    'id-ID': 'id', // +62
    'en-PG': 'pg', // +675
    'fr-CF': 'cf', // +236
    'si-LK': 'lk', // +94
    'uz-UZ': 'uz', // +998
    'km-KM': 'km', // +269, 'km-KM' missing from validator 13.12.0
    // 'tg' // +228 missing from validator 13.12.0 and react-intl-tel-input 8.2.0
    // 'et', // +251 missing from validator 13.12.0 and react-intl-tel-input 8.2.0
    // 'vg', // +1284 missing from validator 13.12.0 and react-intl-tel-input 8.2.0
    // 'bi', // +257 missing from validator 13.12.0 and react-intl-tel-input 8.2.0
  },
  MAX_SMS_BATCH_SIZE: 150,
  MAX_CSV_PARSE_SIZE: 150,
}

export default config
